/**
 * Saisie d'un devis à partir d'une phrase.
 *
 * Sur un chantier, l'artisan dicte plus vite qu'il ne tape : « démolition 25,
 * chape 25, carrelage 30 ». Cette lecture découpe la phrase, y retrouve les
 * articles du catalogue et leurs quantités, et en fait des lignes de devis.
 *
 * Rien n'y est deviné au-delà du raisonnable : ce qui ne correspond à aucun
 * article devient une ligne libre à prix nul, que l'artisan complète. Mieux
 * vaut une ligne à compléter qu'une prestation oubliée.
 */

import {
  aUnComplement,
  coupeLaDictee,
  objetDuTravail,
  placeObjetEtTravail,
  separeLesTravaux,
} from './objet-des-travaux';

/** Nombres écrits en toutes lettres, tels qu'on les dicte. */
const MOTS_NOMBRES = {
  un: 1,
  une: 1,
  deux: 2,
  trois: 3,
  quatre: 4,
  cinq: 5,
  six: 6,
  sept: 7,
  huit: 8,
  neuf: 9,
  dix: 10,
  onze: 11,
  douze: 12,
  treize: 13,
  quatorze: 14,
  quinze: 15,
  seize: 16,
  vingt: 20,
  trente: 30,
  quarante: 40,
  cinquante: 50,
};

/** Unités à retirer d'une désignation libre : elles ne la décrivent pas. */
// Bornées par les lettres accentuées aussi : « prêt » gardait son « t » pour
// une tonne (« Prê à peindre »), et « l'ancien » son « l » pour un litre.
const UNITES =
  /(?<![\p{L}'’])(m2|m²|m3|m³|ml|pces?|sacs?|forfaits?|heures?|u|h|kg|l|t)(?![\p{L}\d'’])/giu;

/**
 * Marqueurs d'une quantité explicite.
 *
 * « 3 pièces » ou « 2 fois » annoncent sans ambiguïté un nombre d'unités. Cette
 * forme l'emporte sur tout autre nombre de la phrase.
 */
const QUANTITE_EXPLICITE =
  /(\d+(?:[.,]\d+)?)\s*(?:pi[eè]ces?|pces?|pcs|unit[eé]s?|\bu\b|fois|\bx\b)/i;

/** Compare sans casse ni accents. */
const sansAccent = (x) =>
  String(x || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const nombre = (x) => Number(String(x).replace(',', '.'));

/** Remplace les nombres dictés en lettres par leur chiffre. */
function chiffreLesMots(texte) {
  let sortie = texte;
  for (const [mot, valeur] of Object.entries(MOTS_NOMBRES)) {
    sortie = sortie.replace(new RegExp('\\b' + mot + '\\b', 'gi'), String(valeur));
  }
  return sortie;
}

/** « 25 », « 25 m² », « 3 pces » : une quantité qui clôt une prestation dictée. */
const FIN_QUANTITE = /\d+(?:[.,]\d+)?\s*(?:m2|m²|m3|m³|ml|m|pces?|sacs?|h|u|kg)?$/i;

/**
 * Coupe sur « et » quand il sépare deux prestations dictées, et non quand il
 * fait partie de la désignation.
 *
 * « démolition 25 et pose 25 » (une quantité avant « et ») et « peinture et
 * plafonds » (un mot de chaque côté) sont deux prestations. « Dépose et
 * évacuation des protections » en est une seule : la couper en deux lignes
 * obligeait l'artisan à les recoller.
 */
function coupeSurEt(segment) {
  const parties = segment.split(/\s+et\s+/i);
  if (parties.length === 1) return parties;
  const unMotChacun = parties.every((x) => x.trim().split(/\s+/).length === 1);
  if (unMotChacun) return parties;
  const sortie = [parties[0]];
  for (const suite of parties.slice(1)) {
    const precedent = sortie[sortie.length - 1];
    if (FIN_QUANTITE.test(precedent.trim())) sortie.push(suite);
    else sortie[sortie.length - 1] = precedent + ' et ' + suite;
  }
  return sortie;
}

/**
 * Découpe une phrase en prestations.
 *
 * La virgule, le point-virgule, le retour à la ligne et le point d'une fin de
 * phrase séparent ; « et » aussi, quand il sépare deux prestations dictées
 * (voir {@link coupeSurEt}).
 */
export function decoupe(phrase) {
  return (
    String(phrase || '')
      .split(/[,;\n]|\.\s+(?=\p{Lu})/u)
      .flatMap(coupeSurEt)
      // « - Déplacement », « • Protection… » : le tiret d'une liste n'est pas du texte.
      .map((x) =>
        x
          .trim()
          .replace(/^[-–—•·*]\s*/, '')
          .replace(/\.+$/, '')
          .trim(),
      )
      .filter(Boolean)
  );
}

/**
 * Retrouve dans le catalogue les articles nommés par un segment.
 *
 * La correspondance porte sur les mots d'au moins quatre lettres du nom de
 * l'article : « carrelage » retrouve « Carrelage 30×60 », là où « de » ou
 * « mur » rapprocheraient n'importe quoi.
 */
function articlesDuSegment(segmentNormalise, catalogue, dejaVus, strict = false) {
  return (catalogue || []).filter((a) => {
    if (dejaVus.has(a.id)) {
      return false;
    }
    const mots = sansAccent(a.nom)
      .split(/[\s,/'\-×x]+/)
      .filter((m) => m.length >= 4);
    // Une phrase de travaux (« Préparation du plafond de la salle de bain »)
    // ne prend un article que s'il en a tous les mots : « Peinture du
    // plafond » n'est pas une préparation.
    if (strict) return mots.length > 0 && mots.every((m) => segmentNormalise.includes(m));
    return mots.some((m) => segmentNormalise.includes(m));
  });
}

/**
 * Quantité à retenir pour un article donné.
 *
 * Le piège est le nom de l'article lui-même : « Carrelage 30×60 » porte deux
 * nombres qui sont des dimensions, pas des quantités. Un segment « carrelage
 * 30×60 sur 25 m² » doit rendre 25, pas 30. Les nombres qui figurent déjà dans
 * le nom sont donc écartés.
 */
function quantitePour(segment, article) {
  const avecChiffres = chiffreLesMots(segment);
  const explicite = avecChiffres.match(QUANTITE_EXPLICITE);
  if (explicite) {
    return nombre(explicite[1]);
  }
  const nombresDuSegment = avecChiffres.match(/\d+(?:[.,]\d+)?/g) || [];
  const nombresDuNom = sansAccent(article?.nom).match(/\d+/g) || [];
  const retenu = nombresDuSegment.find(
    (n) => !nombresDuNom.includes(String(n).replace(/[.,].*/, '')),
  );
  return retenu != null ? nombre(retenu) : 1;
}

/** Quantité d'une prestation libre, sans article de référence. */
function quantiteLibre(segment) {
  const avecChiffres = chiffreLesMots(segment);
  const explicite = avecChiffres.match(QUANTITE_EXPLICITE);
  if (explicite) {
    return nombre(explicite[1]);
  }
  const nombres = avecChiffres.match(/\d+(?:[.,]\d+)?/g) || [];
  return nombres.length ? nombre(nombres[0]) : 1;
}

/** Désignation d'une prestation que le catalogue ne connaît pas. */
function designationLibre(segment) {
  // Les unites partent avant les chiffres : « m2 » contient un chiffre, et
  // l'effacer d'abord laissait un « m » orphelin dans la designation — la
  // ligne s'intitulait « Pose de carrelage m ».
  return segment
    .replace(UNITES, ' ')
    .replace(/\d+([.,]\d+)?/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[\s.]+$/, '')
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * Lit une phrase et en tire des lignes de devis.
 *
 * @param {string} phrase ce qui a été dicté ou écrit
 * @param {Array} catalogue articles de l'entreprise
 * @param {Function} [remisePour] remise applicable à un article, pour ce client
 * @param {{batiment?: boolean}} [options] bâtiment : une prestation libre range
 *   de quoi il s'agit en désignation et ce qu'on fait en description
 * @returns {Array<{d: string, sd: string, u: string, q: number, p: number, rem: number, ref: *}>}
 */
export function litLesTravaux(phrase, catalogue, remisePour, { batiment = false } = {}) {
  const lignes = [];
  const dejaVus = new Set();

  // La dictée vocale n'a pas de ponctuation : au bâtiment, une longue phrase
  // sans virgule se coupe aux mots du travail (voir coupeLaDictee).
  const segments = decoupe(phrase).flatMap((x) =>
    batiment && x.split(/\s+/).length > 10 ? coupeLaDictee(x) : [x],
  );
  for (const segment of segments) {
    // « Préparation du plafond de la salle de bain, masticage, ponçage, prêt
    // à peindre » : les travaux qui suivent une phrase de travaux, sans
    // ouvrage ni mesure, la complètent au lieu d'ouvrir chacun une ligne (ni
    // de prendre « Ponçage parquet » au catalogue).
    const avant = lignes[lignes.length - 1];
    // Un travail qui nomme ce qu'il touche (« Fournitures et application
    // d'une couche d'accrochage ») est à part : il ouvre sa propre ligne.
    if (
      batiment &&
      avant?.suiteOuverte &&
      !/\d/.test(segment) &&
      !objetDuTravail(segment) &&
      !aUnComplement(segment)
    ) {
      const suite = segment.trim().replace(/\.+$/, '');
      avant.sd = avant.sd
        ? avant.sd.replace(/[\s.,;:]+$/, '') + ', ' + suite
        : suite.charAt(0).toUpperCase() + suite.slice(1);
      continue;
    }
    const normalise = sansAccent(segment);
    const phraseDeTravaux = batiment && !!objetDuTravail(designationLibre(segment));
    const trouves = articlesDuSegment(normalise, catalogue, dejaVus, phraseDeTravaux);

    for (const a of trouves) {
      dejaVus.add(a.id);
      lignes.push({
        ...(phraseDeTravaux ? { suiteOuverte: true } : {}),
        d: a.nom,
        sd: a.sd || '',
        u: a.u,
        q: quantitePour(segment, a),
        p: a.p,
        rem: remisePour ? remisePour(a) || 0 : 0,
        ref: a.id,
        // Le taux propre de l'article (magasin : 2.6 % sur l'alimentation).
        ...(a.tva != null ? { tva: +a.tva } : {}),
      });
    }

    if (trouves.length === 0) {
      const d = designationLibre(segment);
      // Un segment réduit à un nombre ou à une unité ne décrit rien : le
      // retenir ajouterait une ligne vide que l'artisan devrait effacer.
      if (d.length >= 2) {
        const rangee = batiment ? placeObjetEtTravail({ d, sd: '' }) : { d, sd: '' };
        lignes.push({
          ...rangee,
          u: 'm²',
          q: quantiteLibre(segment),
          p: 0,
          rem: 0,
          ref: null,
          ...(batiment && rangee.sd ? { suiteOuverte: true } : {}),
        });
      }
    }
  }

  return lignes.map(({ suiteOuverte, ...l }) => l);
}

/* ───────── Offre déjà rédigée ───────── */

/** « 1. Titre », « 2) Titre », « 3 - Titre » : le début d'une prestation numérotée. */
const TITRE_NUMEROTE = /^\s*(\d{1,3})\s*[.)\-–]\s*(\p{L}.*)$/u;

/** Un montant écrit à la suisse ou à la française : 2'530, 2’530, 762.5, 30,5, 1 234.50. */
const MONTANT = "\\d{1,3}(?:['’ ]\\d{3})+(?:[.,]\\d+)?|\\d+(?:[.,]\\d+)?";

/** Unités reconnues avant le « x » d'un calcul, et leur écriture sur l'offre. */
const UNITES_CALCUL = [
  [/^m2$|^m²$/i, 'm²'],
  [/^m3$|^m³$/i, 'm³'],
  [/^ml$|^m$/i, 'ml'],
  [/^h$|^heures?$/i, 'h'],
  [/^j$|^jours?$/i, 'jour'],
  [/^pces?$|^pi[eè]ces?$|^pcs$|^u$|^unit[eé]s?$/i, 'pce'],
  [/^kg$/i, 'kg'],
  [/^t$/i, 't'],
  [/^l$|^litres?$/i, 'l'],
];

/** « 46m2 x 12= 552 », « 30,5 m² × 25 = 762.5 », « 12 h x 80 » : quantité × prix. */
const CALCUL = new RegExp(
  '(' +
    MONTANT +
    ')\\s*(m2|m²|m3|m³|ml|m|h|heures?|j|jours?|pces?|pi[eè]ces?|pcs|u|unit[eé]s?|kg|t|l|litres?)?\\s*[x×*]\\s*(' +
    MONTANT +
    ')\\s*(?:[a-zA-Z€$.]{0,4}\\s*)?(?:=\\s*(' +
    MONTANT +
    ')\\s*(?:[a-zA-Z€$.-]{0,4})?)?',
  'i',
);

/** « Bloc 500 », « Forfait : 1'200 », « forfait 250.- » : un prix global. */
const FORFAIT = new RegExp('\\b(?:bloc|forfait|global|pauschal)\\s*:?\\s*(' + MONTANT + ')', 'i');

/** Un prix seul en fin de texte : « … y compris les meubles. 250 ». */
const PRIX_FINAL = new RegExp(
  '(?:^|\\s)(' + MONTANT + ')\\s*(?:\\.-|-|chf|fr\\.?|€|eur)?\\s*$',
  'i',
);

/** Lit un montant écrit à la suisse : apostrophes et espaces de milliers, virgule décimale. */
export function montantEcrit(texte) {
  const brut = String(texte || '')
    .trim()
    .replace(/['’ ]/g, '');
  return Number(brut.replace(',', '.'));
}

function uniteEcrite(u) {
  if (!u) return 'pce';
  const trouvee = UNITES_CALCUL.find(([re]) => re.test(u));
  return trouvee ? trouvee[1] : u;
}

/** Retire le calcul ou le prix d'une ligne de description, et ce qu'il laisse de ponctuation. */
function sansPrix(ligne, extrait) {
  return ligne
    .replace(extrait, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
}

/**
 * Le prix d'une prestation : le dernier calcul « quantité × prix », sinon un
 * forfait (« Bloc 500 »), sinon un montant seul en fin de texte.
 *
 * @param {string[]} lignes titre puis description de la prestation
 * @returns {{q: number, u: string, p: number, lignes: string[]}} le prix, et les lignes sans lui
 */
function prixDeLaPrestation(lignes) {
  for (let i = lignes.length - 1; i >= 0; i--) {
    // Le dernier calcul de la ligne : « Grès 60x60, … 25m2 x 80 » se lit
    // 25 m² à 80.–, pas 60 pièces à 60.– (la taille du carreau).
    const calculs = [...lignes[i].matchAll(new RegExp(CALCUL.source, 'gi'))];
    const m = calculs[calculs.length - 1];
    if (m) {
      const q = montantEcrit(m[1]);
      const p = montantEcrit(m[3]);
      if (q > 0 && p >= 0) {
        const reste = [...lignes];
        reste[i] = sansPrix(lignes[i], m[0]);
        return { q, u: uniteEcrite(m[2]), p, lignes: reste };
      }
    }
  }
  for (let i = lignes.length - 1; i >= 0; i--) {
    const m = lignes[i].match(FORFAIT);
    if (m) {
      const reste = [...lignes];
      reste[i] = sansPrix(lignes[i], m[0]);
      return { q: 1, u: 'forfait', p: montantEcrit(m[1]), lignes: reste };
    }
  }
  const derniere = lignes.length - 1;
  const m = lignes[derniere].match(PRIX_FINAL);
  // Le titre seul (« 2. Dépose ») ne porte pas de prix : un nombre y serait
  // une dimension, pas un montant.
  if (m && (derniere > 0 || lignes.length === 1)) {
    const reste = [...lignes];
    reste[derniere] = sansPrix(lignes[derniere], m[0]);
    return { q: 1, u: 'forfait', p: montantEcrit(m[1]), lignes: reste };
  }
  return { q: 1, u: 'forfait', p: 0, lignes };
}

/**
 * Lit une offre déjà rédigée, telle qu'on la colle depuis ses notes :
 *
 * <pre>
 * 1. Déplacement et mise en place du chantier
 * Déplacement sur site et installation du chantier. 250
 * 2. Dépose du papier peint
 * Arrachage du papier peint existant. 46m2 x 12= 552
 * 3. Préparation du plafond
 * Ponçage, prêt à peindre. Bloc 500
 * </pre>
 *
 * Chaque titre numéroté ouvre une ligne de l'offre ; les lignes qui suivent
 * en sont la description. Le prix se lit dans le texte : « 46m2 x 12 »
 * (46 m² à 12.–), « Bloc 500 » ou un montant seul en fin de description
 * (forfait). Les prestations n'ont pas à exister dans le catalogue : chacune
 * devient une ligne libre, avec le prix écrit.
 *
 * En bâtiment, chaque prestation range de quoi il s'agit en désignation et ce
 * qu'on fait en description : « 3. Préparation du plafond ⏎ Ponçage, prêt à
 * peindre » devient « Plafond » / « Préparation du plafond, ponçage, prêt à
 * peindre » (objet-des-travaux.js).
 *
 * @param {string} texte le texte collé
 * @param {{batiment?: boolean}} [options] prestations du bâtiment
 * @returns {Array|null} les lignes, ou null si le texte n'est pas une offre numérotée
 */
export function litUneOffreRedigee(texte, { batiment = false } = {}) {
  const lignesDuTexte = String(texte || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const prestations = [];
  for (const ligne of lignesDuTexte) {
    // Un calcul (« 46 m2 x 55 = 2'530 », « 30.5m2 ») commence aussi par un
    // nombre, mais pas suivi d'un point et d'un mot.
    const titre = ligne.match(TITRE_NUMEROTE);
    if (titre) {
      prestations.push([titre[2].trim()]);
    } else if (prestations.length) {
      prestations[prestations.length - 1].push(ligne);
    }
  }
  if (!prestations.length) return null;
  return prestations.map((bloc) => {
    const { q, u, p, lignes } = prixDeLaPrestation(bloc);
    const [titre, ...description] = lignes;
    const lue = {
      d: titre.replace(/[\s.:]+$/, ''),
      sd: description.filter(Boolean).join('\n'),
    };
    const { d, sd } = batiment ? placeObjetEtTravail(lue) : lue;
    return {
      d,
      sd,
      u,
      q,
      p,
      rem: 0,
      ref: null,
    };
  });
}

/* ───────── Travaux dictés avec leur prix ───────── */

/** Les mots d'une monnaie après un prix : « 200 fr », « 60 francs », « 690 CHF », « 250.- » (et « Franken », « franchi »). */
const DEVISE_DICTEE = '(?:francs?(?:\\s+suisses)?|franken|franchi|frs?\\.?|chf|\\.-|€|euros?)';

/** Unités qu'on dicte entre la quantité et le prix : « 30 m² fois 50 fr ». */
const UNITE_DICTEE = [
  'm2',
  'm²',
  'm3',
  'm³',
  'ml',
  'm',
  'h',
  'heures?',
  'j',
  'jours?',
  'pces?',
  /pi[eè]ces?/.source,
  'pcs',
  'u',
  /unit[eé]s?/.source,
  'kg',
  't',
  'l',
  'litres?',
].join('|');

/**
 * Un prix dicté : « 200 fr », ou une quantité fois un prix (« 30 m² fois
 * 50 Fr. », « 12 h x 80 »). Tout y est facultatif sauf le montant : ce qui
 * n'est pas un prix est écarté ensuite (voir {@link estUnPrix}).
 */
const PRIX_DICTE = new RegExp(
  '(?:(' +
    MONTANT +
    ')\\s*(' +
    UNITE_DICTEE +
    ')?\\s*(fois|mal|times|[x×*]|à)\\s*)?(' +
    MONTANT +
    ')\\s*(' +
    DEVISE_DICTEE +
    ')?(?![\\p{L}\\d])',
  'giu',
);

/**
 * Un nombre est un prix quand une monnaie le suit (« 200 fr »), ou quand il
 * suit une quantité et son unité (« 30 m² x 50 ») ou un « fois ». « 60x60 »
 * (la taille d'un carreau) ou « 2 couches » n'en sont pas.
 */
function estUnPrix(m) {
  if (m[5]) return true;
  return !!m[3] && (!!m[2] || /fois|mal|times/i.test(m[3]));
}

/** « Compris protection… » prolonge une phrase : sa majuscule tombe. */
const minuscule = (t) => (/^\p{Lu}\p{Ll}/u.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t);

/** Ce que la dictée écrit en toutes lettres, ramené à l'écriture d'une offre. */
function normaliseLaDictee(texte) {
  return (
    String(texte || '')
      .replace(/m[eè]tres?\s+carr[ée]s?/gi, 'm²')
      .replace(/m[eè]tres?\s+cubes?/gi, 'm³')
      .replace(/m[eè]tres?\s+lin[ée]aires?/gi, 'ml')
      .replace(/\bm\s?2\b/gi, 'm²')
      .replace(/\bm\s?3\b/gi, 'm³')
      // « pharmacie Y, compris évacuation » : la dictée coupe « y compris ».
      .replace(/(^|[\s,])y\s*,?\s*compris\b/gi, '$1y compris')
      .replace(/([^\s,])\s+y compris\b/g, '$1, y compris')
  );
}

/**
 * « 30 m² 10 Fr. » : la quantité et son unité suivies du prix, sans « à » ni
 * « fois » — c'est 30 m² à 10.–, et non un forfait de 10.–.
 */
const QUANTITE_PUIS_PRIX = new RegExp(
  '(' +
    MONTANT +
    ')\\s*(' +
    UNITE_DICTEE +
    ')\\s+(?=(?:' +
    MONTANT +
    ')\\s*' +
    DEVISE_DICTEE +
    '(?![\\p{L}\\d]))',
  'giu',
);

/** Les mots qui mènent au prix (« … concernant 60 fr », « … pour 200 fr ») : ils ne décrivent rien. */
const VERS_LE_PRIX = /(?:\s+(?:concernant|pour|prix|montant|total|environ|soit|de|à|au|=|:))+$/i;

/**
 * Le texte d'une prestation, rangé : l'intitulé avant la première virgule
 * (ou avant « y compris »), le reste en description.
 */
function intituleEtDescription(texte) {
  const propre = texte
    .replace(/^[\s.,;:\-–]+/, '')
    .replace(/[\s.,;:\-–]+$/, '')
    .replace(VERS_LE_PRIX, '')
    .trim();
  const lignes = propre
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lignes.length > 1) {
    return { d: lignes[0].replace(/[\s.,;:]+$/, ''), sd: lignes.slice(1).join('\n') };
  }
  const coupe = propre.match(/^(.+?)(?:\s*,\s*|\s+(?=y compris\b))(.+)$/i);
  if (!coupe) return { d: propre, sd: '' };
  const suite = coupe[2].trim();
  // « y compris évacuation » prolonge l'intitulé : il garde sa minuscule.
  const sd = /^y compris\b/i.test(suite) ? suite : suite.charAt(0).toUpperCase() + suite.slice(1);
  return { d: coupe[1].trim(), sd };
}

/**
 * Lit des travaux dictés ou écrits avec leur prix, une prestation après
 * l'autre :
 *
 * <pre>
 * Déplacement mise en place du chantier 200 fr
 * Protection du couloir jusqu'à la zone de travail 60 fr
 * Dépose ancien sanitaire, baignoire, WC, meuble lavabo, y compris évacuation 690 fr
 * Dépose de l'ancien carrelage, y compris évacuation 30 m² fois 50 fr
 * </pre>
 *
 * Chaque prestation se termine à son prix : les virgules restent dans la
 * prestation (« baignoire, WC, meuble lavabo » est une seule dépose), et
 * le texte dicté d'un trait, sans retour à la ligne, se coupe aussi juste.
 * Un prix seul est un forfait ; « 30 m² fois 50 » est 30 m² à 50.–. Ce qui
 * reste après le dernier prix devient une ligne à chiffrer.
 *
 * @param {string} texte le texte dicté ou collé
 * @param {{batiment?: boolean}} [options] prestations du bâtiment (objet-des-travaux.js)
 * @returns {Array|null} les lignes, ou null si le texte ne porte aucun prix
 */
export function litDesTravauxChiffres(texte, { batiment = false } = {}) {
  const t = normaliseLaDictee(texte).replace(QUANTITE_PUIS_PRIX, '$1 $2 à ');
  const prix = [...t.matchAll(PRIX_DICTE)].filter(estUnPrix);
  if (!prix.length) return null;
  const lignes = [];
  const ajoute = (morceau, q, u, p) => {
    let lue = intituleEtDescription(morceau);
    if (lue.d.length < 2) return;
    lue.d = lue.d.charAt(0).toUpperCase() + lue.d.slice(1);
    let d;
    let sd;
    if (batiment) {
      // L'intitulé se range seul (« Déplacement mise en place du chantier »
      // → « Déplacement » / « Mise en place du chantier »), puis ce qui
      // suivait la virgule le prolonge : « …, compris protection de la zone ».
      const rangee = placeObjetEtTravail({ d: lue.d, sd: '' });
      d = rangee.d;
      sd = !rangee.sd
        ? lue.sd
        : !lue.sd
          ? rangee.sd
          : rangee.sd.replace(/[\s.,;:]+$/, '') +
            (/^y compris\b/i.test(lue.sd) ? ', ' + lue.sd : ', ' + minuscule(lue.sd));
    } else {
      // « Déplacement mise en place du chantier » : le premier travail en
      // titre, la suite en dessous.
      const lu = lue.sd ? lue : separeLesTravaux(lue.d) || lue;
      d = lu.d;
      sd = lu.sd;
    }
    lignes.push({ d, sd, u, q, p, rem: 0, ref: null });
  };
  let debut = 0;
  for (const m of prix) {
    const avecQuantite = m[1] != null;
    ajoute(
      t.slice(debut, m.index),
      avecQuantite ? montantEcrit(m[1]) : 1,
      avecQuantite ? uniteEcrite(m[2]) : 'forfait',
      montantEcrit(m[4]),
    );
    debut = m.index + m[0].length;
  }
  ajoute(t.slice(debut), 1, 'forfait', 0);
  return lignes.length ? lignes : null;
}

/**
 * Les lignes tirées du texte de l'assistant : une offre déjà rédigée
 * (titres numérotés, « 46m2 x 12 », « Bloc 500 ») ou des travaux dictés avec
 * leur prix (« … 200 fr », « 30 m² fois 50 fr ») gardent leurs prix, même pour
 * des prestations absentes du catalogue ; une dictée sans prix retrouve les
 * articles du catalogue.
 *
 * @param {string} texte le texte dicté ou collé
 * @param {object[]} catalogue articles de l'entreprise
 * @param {(article: object) => number} [remisePour] remise du client sur un article
 * @param {{batiment?: boolean}} [options] prestations du bâtiment (objet-des-travaux.js)
 * @returns {Array} les lignes
 */
export function lignesDeLAssistant(texte, catalogue, remisePour, options = {}) {
  return (
    litUneOffreRedigee(texte, options) ||
    litDesTravauxChiffres(texte, options) ||
    litLesTravaux(texte, catalogue, remisePour, options)
  );
}
